import { Button, SelectInput, SelectInputItem, TextField, useAlert } from '@ads/components-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { SerieLangViewModel } from '@/shared/api/generated';
import { RichTextEditor, Textarea } from '@/shared/ui/atoms';
import { FormField } from '@/shared/ui/molecules';

import { useAvailableSerieLanguages } from '../../../hooks/useAvailableSerieLanguages';
import { useSaveSerieLang } from '../../../hooks/useSaveSerieLang';
import { type SerieLangFormValues, serieLangFormSchema } from '../../../types/serieLangForm';

export interface SerieLangFormProps {
  readonly serieId: number;
  /** Idioma em edição; `null` para criação. */
  readonly lang: SerieLangViewModel | null;
  readonly onSaved: () => void;
  readonly onCancel: () => void;
}

const DEFAULT_VALUES: SerieLangFormValues = {
  cultureInfo: '',
  title: '',
  description: '',
  header: '',
};

/**
 * Formulário de Idioma da Série (criar/editar), usado em tela dedicada (não
 * modal). No modo criação, **Idioma** é um `SelectInput` alimentado por
 * `useAvailableSerieLanguages` (só idiomas ainda não cadastrados); na edição, o
 * campo é exibido como texto fixo (não editável — não é possível trocar o
 * idioma de uma tradução existente). Fiel aos frames *Idioma da Série ·
 * Criar/Editar* de `design/series.pen`, incluindo os botões soltos abaixo do card.
 */
export function SerieLangForm({ serieId, lang, onSaved, onCancel }: SerieLangFormProps) {
  const { t } = useTranslation('series');
  const { alert } = useAlert();
  const { save, isPending } = useSaveSerieLang(serieId);
  const { options: availableLanguages } = useAvailableSerieLanguages(serieId);

  const isEdit = lang?.id != null;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SerieLangFormValues>({
    resolver: zodResolver(serieLangFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    reset(
      lang
        ? {
            cultureInfo: lang.cultureInfo ?? '',
            title: lang.title ?? '',
            description: lang.resumee ?? '',
            header: lang.header ?? '',
          }
        : DEFAULT_VALUES,
    );
  }, [lang, reset]);

  const onSubmit = handleSubmit(values => {
    save(values, lang?.id ?? undefined, {
      onSuccess: () => {
        alert({
          alertType: 'success',
          title: t(isEdit ? 'langs.toast.updateSuccess' : 'langs.toast.createSuccess'),
        });
        onSaved();
      },
      onError: () => alert({ alertType: 'warning', title: t('langs.toast.saveError') }),
    });
  });

  return (
    <form className="flex flex-col gap-xs" onSubmit={onSubmit} noValidate>
      <div className="flex flex-col gap-mini rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs">
        <h2 className="ts-heading-sm text-ctx-content-title">
          {t(isEdit ? 'langs.form.editTitle' : 'langs.form.createTitle')}
        </h2>

        {isEdit ? (
          <div className="flex flex-col gap-quark">
            <span className="ts-paragraph-xs !font-medium text-ctx-content-title">
              {t('langs.form.language')}
            </span>
            <span className="ts-paragraph-sm text-ctx-content-title">{lang?.cultureInfo}</span>
          </div>
        ) : (
          <Controller
            control={control}
            name="cultureInfo"
            render={({ field }) => (
              <SelectInput
                label={`${t('langs.form.language')}*`}
                value={field.value}
                onValueChange={field.onChange}
                size="md"
                hasError={Boolean(errors.cultureInfo)}
                {...(errors.cultureInfo ? { helper: t('langs.form.errors.languageRequired') } : {})}
              >
                {availableLanguages.map(option => (
                  <SelectInputItem
                    key={option.key}
                    value={option.key ?? ''}
                    custom={{ root: 'text-nowrap' }}
                  >
                    {option.value}
                  </SelectInputItem>
                ))}
              </SelectInput>
            )}
          />
        )}

        <Controller
          control={control}
          name="title"
          render={({ field }) => (
            <TextField
              label={`${t('langs.form.title')}*`}
              placeholder={t('langs.form.titlePlaceholder')}
              value={field.value}
              onChange={e => field.onChange(e.target.value)}
              size="md"
              fullWidth
              hasError={Boolean(errors.title)}
              {...(errors.title ? { helper: t('langs.form.errors.titleRequired') } : {})}
            />
          )}
        />

        <Controller
          control={control}
          name="description"
          render={({ field }) => (
            <FormField label={t('langs.form.description')}>
              <Textarea
                placeholder={t('langs.form.descriptionPlaceholder')}
                value={field.value}
                onChange={e => field.onChange(e.target.value)}
                rows={3}
              />
            </FormField>
          )}
        />

        <Controller
          control={control}
          name="header"
          render={({ field }) => (
            <FormField label={t('langs.form.header')}>
              <RichTextEditor value={field.value} onChange={field.onChange} minHeight={120} />
            </FormField>
          )}
        />
      </div>

      <div className="flex items-center justify-end gap-nano">
        <Button hierarchy="secondary" size="md" type="button" onClick={onCancel}>
          {t('langs.form.cancel')}
        </Button>
        <Button
          hierarchy="primary"
          size="md"
          type="submit"
          isLoading={isPending}
          disabled={isPending}
        >
          {t('langs.form.save')}
        </Button>
      </div>
    </form>
  );
}
